import React from "react"
import { connect } from "react-redux";
import { withRouter } from 'react-router-dom';
import { Tabs, Table, Avatar, Spin, Button, Modal, Form, Input, InputNumber, Select, Alert, Row, Col, Upload, message, Empty } from 'antd';
import { FilterFilled, PlusOutlined, PaperClipOutlined, SearchOutlined } from '@ant-design/icons';
import { fetchTicketList, fetchTicketCauses, createTicket, createTicketMessage } from '../../actions/actions.js';
import moment from 'moment-business-days';
import './ticketList.css';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

/* L'API espone solo tcIsOpen (booleano): niente stati intermedi come "in lavorazione"/"risolto" */
function ticketStatus(ticket) {
    return ticket.tcIsOpen ? { label: 'Aperto', cls: 'sp-open' } : { label: 'Chiuso', cls: 'sp-closed' };
}

function formatDate(value) {
    if (!value) return '-';
    return moment(value, 'YYYY-MM-DD HH:mm:ss.S').format('DD/MM/YYYY HH:mm');
}

function formatCustomer(ticket) {
    if (ticket.tcCustomerUsername) return ticket.tcCustomerId + ' - ' + ticket.tcCustomerUsername;
    return 'Cliente #' + ticket.tcCustomerId;
}

function formatOperator(ticket) {
    if (ticket.tcOperatorUsername) return ticket.tcOperatorUsername;
    if (ticket.tcOperatorId) return 'Operatore #' + ticket.tcOperatorId;
    return 'Non assegnato';
}

function resolveCauseName(caDescription, causes) {
    const found = (causes || []).find(c => c.caDescription === caDescription);
    if (found) return found.caDescription;
    return caDescription;
}

function initials(name) {
    if (!name) return '';
    return name.split(' ').filter(Boolean).map(w => w[0]).join('').substring(0, 2).toUpperCase();
}

/* Gruppi di campi dinamici (tfId) da mostrare sulla stessa riga nel form di creazione ticket */
const FIELD_ROW_GROUPS = [[3, 4, 5], [6, 2]]; // [Precodice + Articolo + Quantità], [N. ordine + Tipo ordine]

/* Raggruppa i campi di caFieldList in righe (gruppi noti) o campi singoli, preservando l'ordine originale */
function buildCauseFieldLayout(fieldList) {
    const consumed = new Set();
    const layout = [];

    (fieldList || []).forEach((field) => {
        if (consumed.has(field.tfId)) return;

        const group = FIELD_ROW_GROUPS.find((g) => g.includes(field.tfId));
        const groupFields = group && group.map((id) => fieldList.find((f) => f.tfId === id)).filter(Boolean);

        if (group && groupFields.length === group.length) {
            group.forEach((id) => consumed.add(id));
            layout.push({ type: 'row', fields: groupFields });
            return;
        }

        consumed.add(field.tfId);
        layout.push({ type: 'single', field });
    });

    return layout;
}

function renderCauseFieldItem(field) {
    if (field.tfType === 'INFO') {
        return (
            <Alert
                key={field.tfId}
                type="info"
                showIcon
                message={field.tfDescription}
                style={{ marginBottom: 16 }}
            />
        );
    }
    return (
        <Form.Item
            key={field.tfId}
            label={field.tfDescription}
            name={'field_' + field.tfId}
            rules={field.tfRequired ? [{ required: true, message: 'Campo obbligatorio' }] : []}
        >
            {field.tfOptions
                ? (
                    <Select placeholder="Seleziona..." allowClear>
                        {field.tfOptions.split(';').map((opt) => opt.trim()).filter(Boolean).map((opt) => (
                            <Option key={opt} value={opt}>{opt}</Option>
                        ))}
                    </Select>
                )
                : field.tfType === 'NUMBER'
                    ? <InputNumber style={{ width: '100%' }} min={0} />
                    : <Input />}
        </Form.Item>
    );
}

/* tfId sempre esclusi dall'oggetto del ticket (es. Quantità) */
const SUBJECT_EXCLUDED_FIELD_IDS = [5, 7];

/* Coppie di tfId da unire con "-" prima di accodarle al resto dei campi compilati */
const SUBJECT_PAIRS = [[6, 2], [3, 4]];

/* Costruisce l'oggetto del ticket: le coppie note (tipoOrdine-nOrdine, precodice-articolo) vengono unite
   con "-" quando compilate; il risultato, insieme a ogni altro campo compilato non appartenente a una
   coppia, viene poi concatenato con " _ ". I campi 5 e 7 (es. Quantità) sono sempre esclusi. */
function buildTicketSubject(selectedCause, values) {
    const fieldList = (selectedCause && selectedCause.caFieldList) || [];
    const consumed = new Set();

    const valueOf = (tfId) => {
        const v = values['field_' + tfId];
        return (v === undefined || v === null || v === '') ? null : v;
    };

    const parts = [];

    SUBJECT_PAIRS.forEach((ids) => {
        const pairValue = ids.map(valueOf).filter(Boolean).join('-');
        if (pairValue) {
            parts.push(pairValue);
            ids.forEach((id) => consumed.add(id));
        }
    });

    fieldList
        .filter((f) => f.tfType !== 'INFO' && !SUBJECT_EXCLUDED_FIELD_IDS.includes(f.tfId) && !consumed.has(f.tfId))
        .forEach((f) => {
            const v = valueOf(f.tfId);
            if (v) parts.push(v);
        });

    return parts.join(' | ');
}

/* Corpo del primo messaggio: ogni campo compilato (tutti, inclusi 5 e 7) come "Label: valore" su righe separate,
   seguito dal testo libero del campo "Messaggio" */
function buildTicketFirstMessageBody(selectedCause, values) {
    const fieldList = (selectedCause && selectedCause.caFieldList) || [];

    const lines = fieldList
        .filter((f) => f.tfType !== 'INFO')
        .map((f) => {
            const v = values['field_' + f.tfId];
            if (v === undefined || v === null || v === '') return null;
            return f.tfDescription + ': ' + v;
        })
        .filter(Boolean);

    const messageText = (values.firstMessage || '').trim();
    if (messageText) {
        lines.push(messageText);
    }

    return lines.join('\n');
}

function normalizeUploadEvent(e) {
    return Array.isArray(e) ? e : (e && e.fileList);
}

const mapStateToProps = state => {
    return {
        ticketList: state.ticketList,
        ticketCauses: state.ticketCauses,
        ticketCreate: state.ticketCreate,
        user: state.user
    };
};

function mapDispatchToProps(dispatch) {
    return {
        fetchTicketList: (open, startFrom) => dispatch(fetchTicketList(open, startFrom)),
        fetchTicketCauses: () => dispatch(fetchTicketCauses()),
        createTicket: (payload, causeDescription) => dispatch(createTicket(payload, causeDescription)),
        createTicketMessage: (id, body, attachments) => dispatch(createTicketMessage(id, body, attachments))
    };
}

class TicketList extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            ticketList: this.props.ticketList,
            activeTab: 'open',
            unreadOnly: false,
            newTicketModalVisible: false,
            selectedCause: null,
            pageSize: 10
        }

        this.onRowClick = this.onRowClick.bind(this);
        this.toggleUnreadOnly = this.toggleUnreadOnly.bind(this);
        this.isOperator = this.isOperator.bind(this);
        this.isUnread = this.isUnread.bind(this);
        this.openNewTicketModal = this.openNewTicketModal.bind(this);
        this.closeNewTicketModal = this.closeNewTicketModal.bind(this);
        this.handleCauseChange = this.handleCauseChange.bind(this);
        this.handleCreateTicket = this.handleCreateTicket.bind(this);
        this.onShowSizeChange = this.onShowSizeChange.bind(this);
        this.newTicketFormRef = React.createRef();
    }

    componentDidMount() {
        this.props.fetchTicketList(true);
        this.props.fetchTicketList(false);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.ticketList !== prevProps.ticketList) {
            this.setState({
                ...this.state,
                ticketList: this.props.ticketList
            });
        }
    }

    isOperator() {
        return this.props.user.uRole === 'ADMIN';
    }

    isUnread(ticket) {
        return this.isOperator() ? !!ticket.tcUnreadByHelpdesk : !!ticket.tcUnreadByCustomer;
    }

    onRowClick(ticket) {
        this.props.history.push('/ticket/' + ticket.tcId);
    }

    toggleUnreadOnly() {
        this.setState({
            ...this.state,
            unreadOnly: !this.state.unreadOnly
        });
    }

    openNewTicketModal() {
        this.setState({
            ...this.state,
            newTicketModalVisible: true
        });
        this.props.fetchTicketCauses();
    }

    closeNewTicketModal() {
        this.setState({
            ...this.state,
            newTicketModalVisible: false,
            selectedCause: null
        });
        if (this.newTicketFormRef.current) {
            this.newTicketFormRef.current.resetFields();
        }
    }

    handleCauseChange(causeId) {
        const causes = this.props.ticketCauses.data || [];
        const newCause = causes.find((c) => c.caId === causeId) || null;
        const oldCause = this.state.selectedCause;

        if (oldCause && this.newTicketFormRef.current) {
            const oldFieldNames = (oldCause.caFieldList || [])
                .filter((f) => f.tfType !== 'INFO')
                .map((f) => 'field_' + f.tfId);
            if (oldFieldNames.length > 0) {
                this.newTicketFormRef.current.resetFields(oldFieldNames);
            }
        }

        this.setState({
            ...this.state,
            selectedCause: newCause
        });
    }

    handleCreateTicket(values) {
        const selectedCause = this.state.selectedCause;
        const now = moment().format('YYYY-MM-DD HH:mm:ss');
        const tcSubject = buildTicketSubject(selectedCause, values);
        const firstMessage = buildTicketFirstMessageBody(selectedCause, values);

        const payload = {
            tcSubject: tcSubject,
            tcIsOpen: true,
            tcUnreadByHelpdesk: true,
            tcUnreadByCustomer: false,
            tcOpenedOn: now,
            tcLastUpdate: now,
            tcCauseId: values.causeId
        };

        /* antd Upload passa oggetti UploadFile: il File vero sta in originFileObj */
        const attachmentFiles = (values.attachments || [])
            .map((f) => f.originFileObj || f)
            .filter(Boolean);

        this.props.createTicket(payload, selectedCause ? selectedCause.caDescription : undefined).then((created) => {
            const finish = () => {
                this.closeNewTicketModal();

                if (created && created.tcId) {
                    this.props.history.push('/ticket/' + created.tcId);
                } else {
                    this.props.fetchTicketList(true);
                    this.props.fetchTicketList(false);
                }
            };

            if (created && created.tcId) {
                /* Il messaggio di apertura (con o senza allegati) passa sempre da createMessage, mai nel payload di creazione */
                this.props.createTicketMessage(created.tcId, firstMessage, attachmentFiles).then(finish);
            } else {
                finish();
            }
        });
    }

    onShowSizeChange(current, size) {
        this.setState({
            ...this.state,
            pageSize: size
        });
    }

    getRows(tab) {
        const open = this.state.ticketList.open || [];
        const closed = this.state.ticketList.closed || [];

        let rows = [];
        if (tab === 'open') {
            rows = open;
        } else if (tab === 'closed') {
            rows = closed;
        } else {
            rows = [...open, ...closed];
        }

        if (this.state.unreadOnly) {
            rows = rows.filter(this.isUnread);
        }

        return rows;
    }

    /* Stesso pattern di ricerca per colonna usato in Resi (getColumnSearchProps) */
    getColumnSearchProps = (dataIndex, dataIndex2, exact) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={(node) => { this.searchInput = node; }}
                    placeholder="Cerca..."
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90, marginRight: 8 }}>
                    Filtra
                </Button>
                <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1B5EE4' : undefined }} />,
        onFilter: (value, record) => {
            const haystack = dataIndex2
                ? (record[dataIndex] || '') + '-' + (record[dataIndex2] || '')
                : (record[dataIndex] || '');
            if (exact) {
                return haystack.toString().toLowerCase() === value.toString().toLowerCase();
            }
            return haystack.toString().toLowerCase().includes(value.toString().toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
                setTimeout(() => this.searchInput && this.searchInput.select());
            }
        }
    });

    renderTable(tab) {
        const operator = this.isOperator();
        const rows = this.getRows(tab);
        const causes = this.props.ticketCauses.data || [];
        const t = this;

        const columns = [
            {
                title: 'ID',
                key: 'id',
                width: 88,
                render: function (text, ticket) {
                    return <span className="t-id">#{ticket.tcId}</span>;
                },
                ...this.getColumnSearchProps('tcId', null, true)
            },
            operator ? {
                title: 'Cliente',
                key: 'customer',
                width: 200,
                render: function (text, ticket) {
                    return (
                        <div className="t-cell cliente">
                            <span className="cliente-name">{formatCustomer(ticket)}</span>
                        </div>
                    );
                },
                ...this.getColumnSearchProps('tcCustomerUsername', 'tcCustomerId')
            } : null,
            {
                title: 'Oggetto',
                key: 'title',
                width: 320,
                render: function (text, ticket) {
                    const status = ticketStatus(ticket);
                    return (
                        <div className="t-title-wrap">
                            <span className={"t-title" + (t.isUnread(ticket) ? " unread" : "")} title={ticket.tcSubject}>{ticket.tcSubject}</span>
                            <span className={"sp " + status.cls}>{status.label}</span>
                        </div>
                    );
                },
                ...this.getColumnSearchProps('tcSubject', null, false)
            },
            {
                title: 'Causa',
                key: 'cause',
                width: 208,
                render: function (text, ticket) {
                    const name = resolveCauseName(ticket.caDescription, causes);
                    return <span className="t-cell" title={name}>{name}</span>;
                }
            },
            {
                title: 'Operatore',
                key: 'operator',
                width: 200,
                render: function (text, ticket) {
                    return (
                        <div className="t-cell op">
                            <Avatar size={24} className="mini-av">{initials(ticket.tcOperatorUsername)}</Avatar>
                            {formatOperator(ticket)}
                        </div>
                    );
                }
            },
            {
                title: 'Da Leggere',
                key: 'unread',
                width: 96,
                align: 'center',
                render: function (text, ticket) {
                    return t.isUnread(ticket) ? <span className="unread-dot" /> : null;
                }
            },            
            {
                title: 'Ultimo aggiorn.',
                key: 'updatedAt',
                width: 160,
                render: function (text, ticket) {
                    return <span className="t-cell">{formatDate(ticket.tcLastUpdate)}</span>;
                }
            },
            {
                title: 'Aperto il',
                key: 'openedAt',
                width: 136,
                render: function (text, ticket) {
                    return <span className="t-cell">{formatDate(ticket.tcOpenedOn)}</span>;
                }
            }
        ];

        return (
            <Table
                className="ticket-table search-results-table"
                rowKey="tcId"
                columns={columns.filter(Boolean)}
                dataSource={rows}
                scroll={{ x: 1200 }}
                pagination={{
                    pageSize: this.state.pageSize, showSizeChanger: true, onShowSizeChange: this.onShowSizeChange, pageSizeOptions: [5, 10, 15, 30, 50]
                }}
                locale={{ emptyText: this.state.unreadOnly ? 'Nessun ticket non letto in questa sezione.' : 'Nessun ticket in questa sezione.' }}
                onRow={(ticket) => ({ onClick: () => this.onRowClick(ticket) })}
                rowClassName={(ticket) => this.isUnread(ticket) ? 'unread-row' : ''}
            />
        );
    }

    render() {

        const open = this.state.ticketList.open || [];
        const closed = this.state.ticketList.closed || [];
        const activeCauses = (this.props.ticketCauses.data || []).filter(c => c.caActive);
        if (this.props.user.uRole === 'SALESMAN') {
            return <Empty description={"Per utilizzare questa funzionalità, devi impersonificare un cliente"}> </Empty>
        } else {
            return (
                <div className="ticket-list-view">
                    <div className="tabs-bar">
                        <Tabs defaultActiveKey="open" onChange={(k) => this.setState({ ...this.state, activeTab: k })}>
                            <TabPane tab={<span>Aperti <span className="tab-count">{open.length}</span></span>} key="open" />
                            <TabPane tab={<span>Chiusi <span className="tab-count">{closed.length}</span></span>} key="closed" />
                            <TabPane tab={<span>Tutti <span className="tab-count">{open.length + closed.length}</span></span>} key="all" />
                        </Tabs>
                        <Button
                            className={"unread-filter-btn" + (this.state.unreadOnly ? " active" : "")}
                            icon={<FilterFilled />}
                            onClick={this.toggleUnreadOnly}
                        >
                            Solo non letti
                        </Button>
                        {!this.isOperator() && (
                            <Button
                                type="primary"
                                className="new-ticket-btn"
                                icon={<PlusOutlined />}
                                onClick={this.openNewTicketModal}
                            >
                                Nuovo ticket
                            </Button>
                        )}
                    </div>

                    {this.state.ticketList.requestIsPending && open.length === 0 && closed.length === 0 ? (
                        <Spin tip="Caricamento ticket in corso..." style={{ marginLeft: "49%", paddingTop: "2em" }} size="large" />
                    ) : this.renderTable(this.state.activeTab)}

                    <Modal
                        title="Nuova richiesta di assistenza"
                        visible={this.state.newTicketModalVisible}
                        onCancel={this.closeNewTicketModal}
                        onOk={() => this.newTicketFormRef.current.submit()}
                        confirmLoading={this.props.ticketCreate.requestIsPending}
                        okText="Invia richiesta"
                        cancelText="Annulla"
                        destroyOnClose
                    >
                        <Form ref={this.newTicketFormRef} layout="vertical" onFinish={this.handleCreateTicket}>
                            <Form.Item
                                label="Causale"
                                name="causeId"
                                rules={[{ required: true, message: 'Seleziona una causale' }]}
                            >
                                <Select
                                    placeholder="Seleziona la causale della richiesta"
                                    loading={this.props.ticketCauses.requestIsPending}
                                    onChange={this.handleCauseChange}
                                >
                                    {activeCauses.map((cause) => (
                                        <Option key={cause.caId} value={cause.caId}>{cause.caDescription}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            {this.state.selectedCause && buildCauseFieldLayout(this.state.selectedCause.caFieldList).map((item, idx) => {
                                if (item.type === 'row') {
                                    return (
                                        <Row gutter={16} key={'row-' + idx}>
                                            {item.fields.map((field) => (
                                                <Col span={24 / item.fields.length} key={field.tfId}>
                                                    {renderCauseFieldItem(field)}
                                                </Col>
                                            ))}
                                        </Row>
                                    );
                                }
                                return renderCauseFieldItem(item.field);
                            })}

                            <Form.Item
                                label="Messaggio"
                                name="firstMessage"
                                rules={[{ required: true, message: 'Descrivi il problema riscontrato' }]}
                            >
                                <TextArea rows={4} placeholder="Descrivi il problema in dettaglio..." />
                            </Form.Item>

                            <Form.Item
                                label="Allegati"
                                name="attachments"
                                valuePropName="fileList"
                                getValueFromEvent={normalizeUploadEvent}
                            >
                                <Upload beforeUpload={() => false} multiple>
                                    <Button icon={<PaperClipOutlined />}>Allega file</Button>
                                </Upload>
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            )
        }
    }
}


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TicketList));
